import React, { useEffect, useRef, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import PaymentContext from 'Components/Payment/payment-context';
import CVCTooltip from 'Components/Payment/CVCTooltip';
import PaymentButton from 'Components/Payment/PaymentButton';

const messages = defineMessages({
  holderNameError: {
    id: 'payment.card.holder-name-error',
    defaultMessage: 'Card Holder name must be set',
  },
});

const CardPayment = ({ method, intl, isOpen }) => {
  const { checkout, isSubscription, isNewSavedMethod } = useContext(PaymentContext);

  const { formatMessage } = intl;

  const securedFieldsRef = useRef(null);

  const [cardNumberIsValid, setCardNumberIsValid] = useState(false);
  const [cardNumberError, setCardNumberError] = useState(null);
  const [brand, setBrand] = useState(null);

  const [holderNameIsValid, setHolderNameIsValid] = useState(false);
  const [holderName, setHolderName] = useState(null);
  const [holderNameError, setHolderNameError] = useState(null);

  const [expiryMonthIsValid, setExpiryMonthIsValid] = useState(false);
  const [expiryMonthError, setExpiryMonthError] = useState(false);

  const [expiryYearIsValid, setExpiryYearIsValid] = useState(false);
  const [expiryYearError, setExpiryYearError] = useState(false);

  const [securityCodeIsValid, setSecurityCodeIsValid] = useState(false);
  const [securityCodeError, setSecurityCodeError] = useState(null);

  const [buttonAttempted, setButtonAttempted] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [enableOneClick, setEnableOneClick] = useState(false);
  const [encryptedFields, setEncryptedFields] = useState(null);

  const [adyenComponent, setAdyenComponent] = useState(null);

  const { type, name, brands } = method;

  useEffect(() => {
    const options = {
      brands,
      styles: {
        base: {
          color: '#212121',
          fontSize: '15px',
          fontSmoothing: 'antialiased',
          fontWeight: 400,
        },
        error: {
          color: '#e53935',
        },
        placeholder: {
          color: '#d8d8d8',
          fontWeight: 300,
        },
        validated: {
          color: '#43A047',
        },
      },
      onBrand: (state) => {
        if (state.brand) {
          setBrand(state.brand);
        }
      },
      onChange: (state) => {
        if (state.valid.encryptedCardNumber) {
          setCardNumberError(null);
          setCardNumberIsValid(true);
        } else {
          setCardNumberIsValid(false);
        }

        if (state.errors.encryptedCardNumber) {
          setCardNumberIsValid(false);
          setCardNumberError(state.errors.encryptedCardNumber.errorI18n);
        }


        if (state.valid.encryptedExpiryMonth) {
          setExpiryMonthError(null);
          setExpiryMonthIsValid(true);
        } else {
          setExpiryMonthIsValid(false);
        }

        if (state.errors.encryptedExpiryMonth) {
          setExpiryMonthIsValid(false);
          setExpiryMonthError(state.errors.encryptedExpiryMonth.errorI18n);
        }


        if (state.valid.encryptedExpiryYear) {
          setExpiryYearError(null);
          setExpiryYearIsValid(true);
        } else {
          setExpiryYearIsValid(false);
        }

        if (state.errors.encryptedExpiryYear) {
          setExpiryYearIsValid(false);
          setExpiryYearError(state.errors.encryptedExpiryYear.errorI18n);
        }


        if (state.valid.encryptedSecurityCode) {
          setSecurityCodeError(null);
          setSecurityCodeIsValid(true);
        } else {
          setSecurityCodeIsValid(false);
        }

        if (state.errors.encryptedSecurityCode) {
          setSecurityCodeIsValid(false);
          setSecurityCodeError(state.errors.encryptedSecurityCode.errorI18n);
        }


        setEncryptedFields(state.data.paymentMethod);

        setFormIsValid(state.isValid);
      },
      onError: console.error, // eslint-disable-line no-console
    };

    const securedFields = checkout.create('securedfields', options);
    securedFields.mount(securedFieldsRef.current);
    setAdyenComponent(securedFields);
  }, [checkout, brands]);

  useEffect(() => {
    if (adyenComponent && isOpen) {
      adyenComponent.setFocusOn('encryptedCardNumber');
    }
  }, [adyenComponent, isOpen]);

  const toggleEnableOneClick = () => setEnableOneClick(!enableOneClick);

  const updateCardName = (e) => {
    const { value } = e.target;

    if (value && typeof value === 'string') {
      setHolderNameIsValid(true);
      setHolderName(value.trim());
      setHolderNameError(null);
    } else {
      setHolderNameError(formatMessage(messages.holderNameError));
      setHolderNameIsValid(false);
      setHolderName(null);
    }
  };

  const encryptedLabelClick = (fieldName) => {
    adyenComponent.setFocusOn(fieldName);
  };

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  /* eslint-disable jsx-a11y/label-has-for */

  return (
    <div className="payment-option-collapse" ref={securedFieldsRef}>
      <div className="card-fields-container">
        <div className="field-card-number field-card">
          <label onClick={() => encryptedLabelClick('encryptedCardNumber')}>
            <p>
              <FormattedMessage id="payment.payment-form-card-number" defaultMessage="Card Number" />
            </p>
          </label>
          <span id={`card-number-span-${type}`} className={classNames('input-field', { fieldHasError: (buttonAttempted && !cardNumberIsValid) || cardNumberError })} data-cse="encryptedCardNumber" />
          {cardNumberError &&
            <span className="card-field-error">{ cardNumberError }</span>
          }
        </div>

        <div className="field-card-name field-card">
          <label htmlFor={`card-name-${type}`}>
            <p>
              <FormattedMessage id="payment.payment-form-card-name" defaultMessage="Cardholder Name" />
            </p>
          </label>
          <input type="text" id={`card-name-${type}`} placeholder="John Smith" autoComplete="cc-name" className={classNames('input-field', { fieldHasError: (buttonAttempted && !holderNameIsValid) || holderNameError })} onChange={updateCardName} />
          {holderNameError &&
            <span className="card-field-error">{ holderNameError }</span>
          }
        </div>

        <div className="card-fields-second-row-container">
          <div className="field-card-exp-month field-card">
            <label onClick={() => encryptedLabelClick('encryptedExpiryMonth')}>
              <p>
                <FormattedMessage id="payment.payment-form-expiry-month" defaultMessage="Expiry Month" />
              </p>
            </label>
            <span id={`card-month-span-${type}`} className={classNames('input-field', { fieldHasError: (buttonAttempted && !expiryMonthIsValid) || expiryMonthError })} data-cse="encryptedExpiryMonth" />
            {expiryMonthError &&
              <span className="card-field-error">{ expiryMonthError }</span>
            }
          </div>

          <div className="month-year-slash">/</div>

          <div className="field-card-exp-year field-card">
            <label onClick={() => encryptedLabelClick('encryptedExpiryYear')}>
              <p>
                <FormattedMessage id="payment.payment-form-expiry-year" defaultMessage="Expiry Year" />
              </p>
            </label>
            <span id={`card-year-span-${type}`} className={classNames('input-field', { fieldHasError: (buttonAttempted && !expiryYearIsValid) || expiryYearError })} data-cse="encryptedExpiryYear" />
            {expiryYearError &&
              <span className="card-field-error">{ expiryYearError }</span>
            }
          </div>

          <div className="field-card-cvc field-card">
            <label onClick={(e) => { e.preventDefault(); encryptedLabelClick('encryptedSecurityCode'); }}>
              <CVCTooltip brand={brand} />
            </label>
            <span id={`card-cvc-span-${type}`} className={classNames('input-field', { fieldHasError: (buttonAttempted && !securityCodeIsValid) || securityCodeError })} data-cse="encryptedSecurityCode" />
            {securityCodeError &&
              <span className="card-field-error">{ securityCodeError }</span>
            }
          </div>
        </div>
      </div>
      {!isNewSavedMethod &&
        <div className="remember-tick-container">
          <label htmlFor="remember">
            <input disabled={isSubscription} type="checkbox" id="remember" name="remember" defaultChecked={isSubscription ? true : enableOneClick} onChange={() => toggleEnableOneClick()} />
            <FormattedMessage id="payment.payment-form-remember-card" defaultMessage="Remember Card Details" />
          </label>
        </div>
      }
      <div className="complete-button-container">
        <PaymentButton
          name={name}
          type={type}
          isDisabled={!formIsValid || !holderNameIsValid}
          disabledAction={() => setButtonAttempted(true)}
          paymentMethod={{ ...encryptedFields, holderName }}
          enableOneClick={enableOneClick}
          adyenComponent={adyenComponent}
        />
      </div>
    </div>
  );
};

CardPayment.propTypes = {
  method: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default injectIntl(CardPayment);
